import React from "react";

/*
 * SkylineBackdrop, decorative campus halftone illustration anchored at the
 * bottom of any section. Each variant ships a custom mask + opacity tuned
 * for the content that sits in front of it.
 *
 * Mobile readability: the scrim is intentionally aggressive at the top of
 * the section so floating headlines / body copy / CTA buttons stay sharp
 * even on small screens. The image is still very much present at the
 * bottom of the section where there is no text.
 */

const BACKDROPS = {
  princeton: {
    src: "/skylines/princeton.jpg",
    alt: "Princeton University campus",
    defaultHeight: "min(110%, 880px)",
    // Two-peak halftone: image visible at top (towers) AND at the very bottom
    // (below the CTAs). White plate sits only over the paragraph + CTA band.
    mask: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 14%, rgba(0,0,0,0.85) 28%, rgba(0,0,0,0.45) 48%, rgba(0,0,0,0.65) 72%, rgba(0,0,0,0.9) 88%, #000 100%)",
    defaultOpacity: 0.36,
    // White scrim peaks around the paragraph + CTA band (52-65%), then
    // releases so the image is visible again at the bottom of the hero.
    scrim: "linear-gradient(to bottom, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.32) 22%, rgba(255,255,255,0.18) 35%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.78) 62%, rgba(255,255,255,0.45) 78%, rgba(255,255,255,0.15) 90%, rgba(255,255,255,0) 100%)",
  },
  stanford: {
    src: "/skylines/stanford.jpg",
    alt: "Stanford University Hoover Tower and campus",
    defaultHeight: "min(110%, 920px)",
    mask: "linear-gradient(to bottom, transparent 0%, transparent 28%, rgba(0,0,0,0.55) 46%, rgba(0,0,0,0.9) 66%, #000 88%)",
    defaultOpacity: 0.42,
    scrim: "linear-gradient(to bottom, rgba(249,250,251,0.85) 0%, rgba(249,250,251,0.55) 24%, rgba(249,250,251,0.2) 48%, rgba(249,250,251,0) 70%)",
  },
  umich: {
    src: "/skylines/umich.jpg",
    alt: "University of Michigan, Ann Arbor campus aerial view",
    defaultHeight: "min(110%, 820px)",
    mask: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 14%, #000 28%, #000 100%)",
    defaultOpacity: 0.38,
    scrim: "linear-gradient(to bottom, rgba(249,250,251,0.7) 0%, rgba(249,250,251,0.25) 30%, rgba(249,250,251,0) 60%)",
  },
};

// Backwards-compatible aliases.
BACKDROPS.sf = BACKDROPS.princeton;
BACKDROPS.ny = BACKDROPS.stanford;

export default function SkylineBackdrop({ which = "princeton", height, opacity }) {
  const s = BACKDROPS[which] || BACKDROPS.princeton;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 z-0 overflow-hidden"
      style={{
        height: height || s.defaultHeight,
        WebkitMaskImage: s.mask,
        maskImage: s.mask,
      }}
      data-testid={`skyline-backdrop-${which}`}>
      <img
        src={s.src}
        alt={s.alt}
        loading="lazy"
        decoding="async"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-none select-none"
        style={{
          opacity: opacity ?? s.defaultOpacity,
          objectFit: "cover",
          objectPosition: "center bottom",
          minWidth: "640px",
          height: "100%",
        }}
        draggable={false}
      />
      {s.scrim && (
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: s.scrim }}
        />
      )}
    </div>
  );
}
